// Vertical-template data for the operator console (mockup 47 — "Plantillas
// verticales"). The foundation every bot in an industry inherits: intent
// categories, sensitivity rules, base handoff, default tone, base skills,
// compliance. Editing a template changes every client in that industry, so
// changes are versioned and published on purpose. Admin-only.
//
// Wired: getVerticalTemplates() -> GET /admin/templates and
// getVerticalTemplate() -> GET /admin/templates/:id. The endpoint projects each
// vertical_templates row (a full BotConfig) into this shape: `defines`
// summarizes the dimensions, `sensitivityRules` surfaces the priority triggers,
// `botsInheriting` counts the bots in that vertical.

import { apiClient } from '../../api/client.js'

export type VerticalKey = 'salud' | 'empeno' | 'autos_nuevos' | 'autos_seminuevos' | 'medico'

export interface TemplateDefine {
  text: string
  /** Applies always; the bot config can't override it. */
  nonNegotiable?: boolean
}

export type RuleLevel = 'non-negotiable' | 'tunable'
export interface SensitivityRule {
  id: string
  title: string
  level: RuleLevel
  description: string
  patterns: string[]
  example: string
  action: string
  /** The crisis/ER redirect copy shown to the person — the one editable field. */
  message: string
  enabled: boolean
}

export interface VerticalTemplate {
  id: string
  vertical: VerticalKey
  name: string
  version: string
  editedMeta: string
  botsInheriting: number
  defines: TemplateDefine[]
  /** The "Reglas de sensibilidad" editor tab (mockup 47, view 2). */
  sensitivityRules: SensitivityRule[]
}

/** The editor's dimension tabs (mockup 47, view 2). */
export const EDITOR_TABS = [
  { id: 'clasificacion', label: 'Clasificación' },
  { id: 'sensibilidad', label: 'Reglas de sensibilidad' },
  { id: 'handoff', label: 'Handoff base' },
  { id: 'personalidad', label: 'Personalidad' },
  { id: 'habilidades', label: 'Flujos base' },
  { id: 'compliance', label: 'Compliance' },
]

/**
 * GET /admin/templates — every vertical template projected from its full
 * BotConfig row (defines, sensitivity rules, inheriting-bot counts).
 */
export async function getVerticalTemplates(): Promise<VerticalTemplate[]> {
  const res = await apiClient.get<{ data: VerticalTemplate[] }>('/admin/templates')
  return res.data.data
}

/**
 * GET /admin/templates/:id — one template's editor projection. Resolves null
 * on 404 (unknown/inactive) so the page renders its not-found state.
 */
export async function getVerticalTemplate(id: string): Promise<VerticalTemplate | null> {
  try {
    const res = await apiClient.get<{ data: VerticalTemplate }>(
      `/admin/templates/${encodeURIComponent(id)}`,
    )
    return res.data.data
  } catch {
    // 404 (unknown / inactive template) -> the page renders its not-found state.
    return null
  }
}

/** Create a new template by cloning an existing one (new name, same industry +
 *  config). Returns the new template's id. INHERTA staff only (server-gated). */
export async function createVerticalTemplate(input: {
  name: string
  description?: string
  sourceId: string
}): Promise<{ id: string; name: string }> {
  const res = await apiClient.post<{ data: { id: string; name: string } }>(
    '/admin/templates',
    input,
  )
  return res.data.data
}

/** Rename / re-describe a template. */
export async function renameVerticalTemplate(
  id: string,
  patch: { name?: string; description?: string },
): Promise<void> {
  await apiClient.patch(`/admin/templates/${encodeURIComponent(id)}`, patch)
}

/** One priority-trigger edit: the crisis/ER redirect copy, keyed by rule id. */
export interface PublishTrigger {
  id: string
  message: string
}

export interface PublishResult {
  ok: true
  /** The template's new version number after the bump. */
  version: number
  /** How many inheriting bots had their config cache invalidated. */
  botsInvalidated: number
}

/** Publish edited safety-redirect copy: persists each trigger's `message`, bumps
 *  the template version, and invalidates the config cache of every inheriting
 *  bot. platform_admin+ only (the API 403s otherwise). */
export async function publishTemplate(
  id: string,
  triggers: PublishTrigger[],
): Promise<PublishResult> {
  const res = await apiClient.post<{ data: PublishResult }>(
    `/admin/templates/${encodeURIComponent(id)}/publish`,
    { triggers },
  )
  return res.data.data
}
